import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { LngLatBoundsLike } from "maplibre-gl";
import { Geometry } from "geojson";

import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { CloseIcon, CompareTableIcon } from "@/assets/icons";
import { SPATIAL_ANALYSIS_RESULT_DATASET_NAME } from "@/constants/dataset";
import { CATCHMENT_SOURCE_IDS, COORDINATE_LABLE } from "@/constants/map";
import { useMap } from "@/hooks/map/core/useMapContext";
import {
  BOUNDS_PADDING,
  TO_BOUNDS_DURATION
} from "@/hooks/map/interactions/useMapInteractions";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import { setSelectedDataset } from "@/store/slice/datasetSlice";
import {
  setComparisonFeature,
  setSelectedFeature
} from "@/store/slice/mapSlice";
import {
  resetUIDataConfigExceptSidebarMenu,
  setShowComparisonPopup,
  toggleSpatialAnalysisResultTabularModal
} from "@/store/slice/uiSlice";
import {
  DataSchemaType,
  Dataset,
  DatasetDataType,
  DatasetSchemaLabel,
  SchemaToDatasetMap
} from "@/types/dataset";
import { SelectedFeature } from "@/types/map";
import { EOutputAnalysisType } from "@/types/spatial";
import {
  getAvailableDatasets,
  transformPropertiesWithCategory,
  transformVisibleAttributes
} from "@/utils/dataset";
import { hideAllLayersWithPrefixes } from "@/utils/map";
import { parseFeaturePhotoValue } from "@/utils/string";

import { bbox } from "@turf/turf";
import { Button } from "equator-design-system";
import { ChevronDown, ChevronUp } from "lucide-react";

import FeatureInfoItem from "./FeatureInfoItem";
import { ParsedFeaturePhotoValue } from "./types";
import {
  buildAdminAreaAttribute,
  findItemById,
  getAttributePriority,
  getPhotoTypePriority,
  groupAttributesByPriority,
  GroupedAttributes,
  sortPrimaryAttributes
} from "./utils";

export interface SelectedAttribute {
  query: string[] | null;
  query_type: DatasetDataType[] | null;
  query_column: string[] | null;
}

interface FeatureInfoProps {
  selectedFeature: SelectedFeature | null;
  setSelectedAttribute: (params: SelectedAttribute | null) => void;
}

type PhotoCandidate = {
  key: string;
  parsed: ParsedFeaturePhotoValue;
  orderIndex: number;
  originalIndex: number;
  typePriority: number;
};

const excludedKeys = new Set([
  "point_count",
  "point_count_abbreviated",
  "unique_id",
  "cluster",
  "color",
  "opacity",
  "width"
]);

const adminAreaKeys = [
  "province_name",
  "city_name",
  "district_name",
  "village_name"
];

const ADMIN_AREA_LABEL = new Set([
  "Village Name",
  "District Name",
  "City Name",
  "Province Name"
]);

export const FeatureInfo: React.FC<FeatureInfoProps> = ({
  selectedFeature,
  setSelectedAttribute
}: FeatureInfoProps) => {
  const dispatch = useDispatch();
  const selectedFeatureRef = useRef(selectedFeature);
  const { isPOIInfoEnabled, isTabularViewAnalysisResultEnabled } =
    useAppFeatures();

  const {
    map,
    handleClearSelectedState,
    zoomLevel,
    hideAllMarkers,
    showOnlyLayer,
    deleteAllFeatures
  } = useMap();

  const { datasets, rawDatasets, visualizationOrder } = useSelector(
    (state: RootState) => state.dataset
  );
  const { showAnalysisResultPopup } = useSelector(
    (state: RootState) => state.ui
  );

  const availableDatasets = getAvailableDatasets(datasets);
  const orderedDatasets = useMemo(
    () => visualizationOrder.map((id) => datasets[id]).filter(Boolean),
    [visualizationOrder, datasets]
  );

  const spatialAnalysisDatasetId =
    orderedDatasets.find(
      (dataset) => dataset?.data?.name === SPATIAL_ANALYSIS_RESULT_DATASET_NAME
    )?.data?.id ?? null;
  const spatialAnalysisRawDataset =
    spatialAnalysisDatasetId && rawDatasets
      ? rawDatasets[spatialAnalysisDatasetId]
      : undefined;
  const isSiteProfiling =
    spatialAnalysisRawDataset?.spatial_aggregation_type ===
    EOutputAnalysisType.PROFILING;

  const { data: userProfile } = useGetUserProfileAPI();

  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    // Update ref to current feature
    if (selectedFeature) {
      selectedFeatureRef.current = selectedFeature;
    }
    if (!selectedFeatureRef.current || selectedFeature) return;
    handleClearSelectedState(selectedFeatureRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeature]);

  if (!selectedFeature) return null; // Early return if no feature is selected

  const handleClose = () => {
    handleClearSelectedState(selectedFeature);

    dispatch(setSelectedFeature(null));
  };

  const handleZoomToTarget = (geometry: Geometry) => {
    const calcBbox = bbox(geometry);

    map?.fitBounds(calcBbox as LngLatBoundsLike, {
      padding: BOUNDS_PADDING,
      maxZoom: zoomLevel,
      duration: TO_BOUNDS_DURATION
    });
  };

  const handleClickComparsion = () => {
    dispatch(resetUIDataConfigExceptSidebarMenu());
    dispatch(setSelectedDataset(currentDataset as Dataset));
    dispatch(
      setShowComparisonPopup({
        isFromAnalysisResult: showAnalysisResultPopup as boolean,
        isShow: true,
        isLimitShow: false
      })
    );
    dispatch(
      setComparisonFeature([
        {
          properties: selectedFeature.properties,
          id: selectedFeature?.id ?? "",
          source: selectedFeature?.source || "",
          geometry: selectedFeature.geometry
        }
      ])
    );
    showOnlyLayer(currentDataset?.data.id || "");
    handleZoomToTarget(selectedFeature.geometry);

    dispatch(setSelectedFeature(null));
    deleteAllFeatures();
    if (!isSiteProfiling) {
      hideAllMarkers();
      if (map) {
        hideAllLayersWithPrefixes(map, CATCHMENT_SOURCE_IDS);
      }
    }
  };

  const handleAttributeSelected = ({
    key,
    value,
    dataType
  }: {
    key: string;
    value: unknown;
    dataType?: DataSchemaType | DatasetSchemaLabel;
  }) => {
    const query = [String(value)];
    setSelectedAttribute({
      query: query,
      query_type: dataType
        ? [SchemaToDatasetMap[dataType as DataSchemaType]]
        : null,
      query_column: [key.toLowerCase().split(" ").join("_")]
    });

    dispatch(setSelectedDataset(currentDataset!));
    dispatch(toggleSpatialAnalysisResultTabularModal());
  };

  const currentDataset = findItemById(
    availableDatasets,
    selectedFeature.source
  );

  const transformedProperties = transformPropertiesWithCategory(
    selectedFeature?.properties as SelectedFeature,
    currentDataset?.categories ?? []
  );

  const selectedCategory = currentDataset?.selectedCategory;
  const title = transformedProperties.find(
    (item) => item.data_type === DataSchemaType.TITLE
  )?.value;

  const datasetDisplayName =
    currentDataset?.data.display_name ?? currentDataset?.data.name ?? null;
  const isPOIDataset = currentDataset?.properties?.spatialType === "poi";
  const isPOIEnhancementEnabled = isPOIInfoEnabled && isPOIDataset;

  const displayTitle =
    typeof title === "string" && title.trim().length > 0
      ? title
      : (datasetDisplayName ?? selectedFeature?.id ?? "");

  const resultTransform = transformVisibleAttributes(
    transformedProperties,
    userProfile?.timezone
  );

  const geometryCategoryIndex = currentDataset?.categories.findIndex(
    (item) => item.data_type === DataSchemaType.GEOMETRY
  );

  const attributes = selectedFeature?.properties
    ? Object.entries(selectedFeature.properties).filter(
        ([key]) => !excludedKeys.has(key)
      )
    : [];

  const normalizeKey = (key: string) => key.replace(/\s+/g, "_").toLowerCase();
  const attributeEntriesByNormalizedKey = new Map<
    string,
    { key: string; value: unknown; index: number }
  >();

  attributes.forEach(([key, value], index) => {
    attributeEntriesByNormalizedKey.set(normalizeKey(key), {
      key,
      value,
      index
    });
  });

  if (
    geometryCategoryIndex !== undefined &&
    geometryCategoryIndex !== -1 &&
    selectedFeature?.geometry?.coordinates &&
    selectedFeature.geometry.coordinates.length >= 2
  ) {
    if (geometryCategoryIndex <= attributes.length) {
      attributes.splice(geometryCategoryIndex, 0, [
        COORDINATE_LABLE.LATITUDE,
        selectedFeature.geometry.coordinates[1]
      ]);
      attributes.splice(geometryCategoryIndex + 1, 0, [
        COORDINATE_LABLE.LONGITUDE,
        selectedFeature.geometry.coordinates[0]
      ]);
    }
  }

  // Move the item with selectedCategory key to the beginning.
  if (selectedCategory) {
    const selectedItemIndex = attributes.findIndex(
      ([key]) => key === selectedCategory.attribute_name
    );
    if (selectedItemIndex > 0) {
      const selectedItem = attributes[selectedItemIndex];
      attributes.splice(selectedItemIndex, 1);
      attributes.unshift(selectedItem);
    }
  }

  const attributeTypeMap = new Map(
    transformedProperties.map(({ key, data_type, data_label }) => {
      // Use data_label for special cases like Address, because its data_type is "string"
      // maybe need refactor later if more special cases arise
      const type =
        data_label === DatasetSchemaLabel.ADDRESS ? data_label : data_type;

      return [key, type];
    })
  );

  const categoryOrderMap = new Map(
    (currentDataset?.categories ?? []).map((category, index) => [
      category.attribute_name,
      index
    ])
  );

  const selectedPhotoCandidate = attributes.reduce<PhotoCandidate | null>(
    (bestCandidate, [key, value], index) => {
      const dataType = attributeTypeMap.get(key);
      if (dataType !== DataSchemaType.PHOTOS) return bestCandidate;

      const parsed = parseFeaturePhotoValue(value as string);

      if (!parsed || parsed.items.length === 0) return bestCandidate;

      const candidate: PhotoCandidate = {
        key,
        parsed,
        orderIndex: categoryOrderMap.get(key) ?? index,
        originalIndex: index,
        typePriority: getPhotoTypePriority(parsed.type)
      };

      if (!bestCandidate) return candidate;

      if (candidate.typePriority < bestCandidate.typePriority) return candidate;
      if (candidate.typePriority > bestCandidate.typePriority)
        return bestCandidate;
      if (candidate.orderIndex < bestCandidate.orderIndex) return candidate;
      if (candidate.orderIndex > bestCandidate.orderIndex) return bestCandidate;

      return candidate.originalIndex < bestCandidate.originalIndex
        ? candidate
        : bestCandidate;
    },
    null
  );

  const sortedAttributes = attributes
    .map(([key, value], index) => ({
      entry: [key, value],
      key,
      dataType: attributeTypeMap.get(key),
      orderIndex: categoryOrderMap.get(key) ?? index,
      originalIndex: index
    }))
    .sort((a, b) => {
      const priorityDiff =
        getAttributePriority(a.dataType) - getAttributePriority(b.dataType);

      if (priorityDiff !== 0) return priorityDiff;

      if (
        a.dataType === DataSchemaType.PHOTOS &&
        b.dataType === DataSchemaType.PHOTOS &&
        selectedPhotoCandidate
      ) {
        if (a.key === selectedPhotoCandidate.key) return -1;
        if (b.key === selectedPhotoCandidate.key) return 1;
      }

      const orderDiff = a.orderIndex - b.orderIndex;

      if (orderDiff !== 0) return orderDiff;

      return a.originalIndex - b.originalIndex;
    })
    .map(({ entry }) => entry);

  const currAttributes = isPOIEnhancementEnabled
    ? sortedAttributes
    : attributes;

  const ATTRIBUTE_PREVIEW_LIMIT = 6;

  let visibleAttributes = expanded
    ? currAttributes
    : currAttributes.slice(0, ATTRIBUTE_PREVIEW_LIMIT);

  if (isPOIEnhancementEnabled) {
    const addressAttribute = currAttributes.find(
      ([key]) => attributeTypeMap.get(key) === DataSchemaType.ADDRESS
    );

    if (addressAttribute) {
      const isAddressVisible = visibleAttributes.some(
        ([key]) => key === addressAttribute[0]
      );

      if (!isAddressVisible) {
        const combined = [addressAttribute, ...visibleAttributes];

        visibleAttributes = combined
          .filter(
            (entry, index, self) =>
              self.findIndex(([key]) => key === entry[0]) === index
          )
          .slice(0, ATTRIBUTE_PREVIEW_LIMIT);
      }
    }
  }

  const displayAttributes = visibleAttributes
    .map(([key, value], index) => {
      const dataType = attributeTypeMap.get(key);

      const isSelectedPhoto =
        dataType === DataSchemaType.PHOTOS &&
        selectedPhotoCandidate?.key === key;

      return {
        key,
        value: Object.prototype.hasOwnProperty.call(resultTransform, key)
          ? resultTransform[key]
          : value,
        dataType,
        originalIndex: index,
        ...(isSelectedPhoto &&
          isPOIEnhancementEnabled && {
            photos: selectedPhotoCandidate?.parsed.items
          })
      };
    })

    .filter((item) => {
      if (item.dataType === DataSchemaType.PHOTOS) {
        if (!isPOIEnhancementEnabled) return true;
        return (item.photos?.length ?? 0) > 0;
      }
      return true;
    });

  let groupedAttributes: GroupedAttributes | null = null;

  if (isPOIEnhancementEnabled) {
    // Group attributes into primary and secondary
    groupedAttributes = groupAttributesByPriority(
      displayAttributes,
      ADMIN_AREA_LABEL
    );

    // Build and add admin area attribute if available
    const adminAreaAttribute = buildAdminAreaAttribute(
      adminAreaKeys,
      attributeEntriesByNormalizedKey,
      normalizeKey,
      resultTransform
    );

    if (adminAreaAttribute) groupedAttributes.primary.push(adminAreaAttribute);

    // Sort primary attributes by type order and original position
    groupedAttributes.primary = sortPrimaryAttributes(
      groupedAttributes.primary
    );
  }

  return (
    <div className="flex flex-col absolute top-[24px] right-[5rem] bg-white rounded-xl p-4 z-10 max-w-[340px] w-full max-h-[90vh] overflow-hidden shadow-[0_4px_24px_5px_rgba(206,206,206,0.40)]">
      <div className="flex flex-row items-start justify-between h-[40px]">
        <p
          className="text-txtHeading font-semibold text-xl truncate"
          title={String(displayTitle)}
        >
          {displayTitle}
        </p>
        <div className="flex">
          <Button
            aria-label="Compare Feature"
            className="h-6 px-1 fill-dim-gray ml-auto"
            onClick={handleClickComparsion}
          >
            <CompareTableIcon title="Compare Feature" />
          </Button>
          <Button
            className="h-6 px-1 fill-dim-gray ml-auto"
            onClick={handleClose}
          >
            <CloseIcon />
          </Button>
        </div>
      </div>

      <div className="flex my-2 flex-col gap-3 max-h-[90vh-100px] overflow-y-auto custom-scrollbar">
        {isPOIEnhancementEnabled && groupedAttributes ? (
          <>
            {groupedAttributes.primary.map(
              ({ key, dataType, value, photos }) => (
                <FeatureInfoItem
                  key={key}
                  attrKey={key}
                  value={value}
                  dataType={dataType}
                  photos={photos}
                  isPOIDataset={isPOIDataset}
                  showSelectAttributeIcon={
                    !!showAnalysisResultPopup &&
                    !!isTabularViewAnalysisResultEnabled
                  }
                  onAttributeSelected={handleAttributeSelected}
                />
              )
            )}
            {expanded &&
              groupedAttributes.primary.length > 0 &&
              groupedAttributes.secondary.length > 0 && (
                <div
                  className="h-px flex-shrink-0 my-2"
                  style={{ backgroundColor: "#E1E1E1" }}
                />
              )}
            {expanded &&
              groupedAttributes.secondary.map(
                ({ key, dataType, value, photos }) => (
                  <FeatureInfoItem
                    key={key}
                    attrKey={key}
                    value={value}
                    dataType={dataType}
                    photos={photos}
                    isPOIDataset={isPOIDataset}
                    showSelectAttributeIcon={
                      !!showAnalysisResultPopup &&
                      !!isTabularViewAnalysisResultEnabled
                    }
                    onAttributeSelected={handleAttributeSelected}
                  />
                )
              )}
          </>
        ) : (
          displayAttributes.map(({ key, dataType, value, photos }) => (
            <FeatureInfoItem
              key={key}
              attrKey={key}
              value={value}
              dataType={dataType}
              photos={photos}
              isPOIDataset={isPOIDataset}
              showSelectAttributeIcon={
                !!showAnalysisResultPopup &&
                !!isTabularViewAnalysisResultEnabled
              }
              onAttributeSelected={handleAttributeSelected}
            />
          ))
        )}
      </div>
      {sortedAttributes.length > 6 && (
        <div className="flex items-center justify-center h-[30px]">
          <Button
            className="mt-2 text-sm font-semibold"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <>
                <span>Collapse</span>
                <ChevronUp width={16} />
              </>
            ) : (
              <>
                <span>Expand</span>
                <ChevronDown width={16} />
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};
